import React, { useState, useEffect, useCallback } from 'react';
import api, { extractApiError } from '../../services/api';
import toast from 'react-hot-toast';
import CredentialForm from '../../components/admin/CredentialForm';
import DistanciaCredentialForm from '../../components/admin/DistanciaCredentialForm';
import ConfirmModal from '../../components/common/ConfirmModal';

const CARRIER_ICONS = {
  correios: '📮',
  jadlog: '🚚',
  loggi: '🛵',
  'jt-express': '✈️',
  'distancia-mapbox': '📏',
};

const CARRIER_COLORS = {
  correios: { bg: '#fff7ed', border: '#fed7aa', accent: '#ea580c' },
  jadlog: { bg: '#eff6ff', border: '#bfdbfe', accent: '#2563eb' },
  loggi: { bg: '#f0fdf4', border: '#bbf7d0', accent: '#16a34a' },
  'jt-express': { bg: '#fdf4ff', border: '#e9d5ff', accent: '#9333ea' },
  'distancia-mapbox': { bg: '#ecfeff', border: '#a5f3fc', accent: '#0891b2' },
};

function defaultColors() {
  return { bg: '#f9fafb', border: '#e5e7eb', accent: '#6b7280' };
}

export default function FreightPage() {
  const [carriers, setCarriers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [panel, setPanel] = useState('config'); // 'config' | 'credentials'
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);

  const fetchCarriers = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/freight/carriers');
      // envelope: { success, data: { carriers: [] }, message }
      const list = data.data.carriers;
      setCarriers(list);
      if (selected) {
        const updated = list.find((c) => c.id === selected.id);
        if (updated) setSelected(updated);
      }
    } catch {
      toast.error('Erro ao carregar transportadoras');
    } finally {
      setLoading(false);
    }
  }, [selected]);

  useEffect(() => { fetchCarriers(); }, []); // eslint-disable-line

  async function handleToggle(carrier, e) {
    e.stopPropagation();
    try {
      const { data } = await api.patch(`/freight/carriers/${carrier.id}/toggle`);
      // envelope: { success, data: { isEnabled }, message }
      const isEnabled = data.data.isEnabled;
      toast.success(isEnabled ? `${carrier.name} ativada` : `${carrier.name} desativada`);
      fetchCarriers();
    } catch (err) {
      toast.error(extractApiError(err, 'Erro ao alterar status'));
    }
  }

  async function handleSaveConfig(formData) {
    setSaving(true);
    try {
      await api.put(`/freight/carriers/${selected.id}/config`, formData);
      toast.success('Configuração salva com sucesso!');
      fetchCarriers();
    } catch (err) {
      toast.error(extractApiError(err, 'Erro ao salvar'));
    } finally {
      setSaving(false);
    }
  }

  async function handleSaveCredentials(credentials) {
    setSaving(true);
    try {
      await api.put(`/freight/carriers/${selected.id}/config`, {
        useOwnCredentials: true,
        credentials,
      });
      toast.success('Credenciais salvas com sucesso!');
      fetchCarriers();
    } catch (err) {
      const details = err.response?.data?.fields;
      toast.error(details
        ? `Campos obrigatórios: ${details.join(', ')}`
        : extractApiError(err, 'Erro ao salvar credenciais')
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleTest() {
    setTesting(true);
    try {
      const { data } = await api.post(`/freight/carriers/${selected.id}/test`);
      // controller retorna: { success: result.ok, data: null, message: result.message }
      if (data.success) {
        toast.success(data.message);
      } else {
        toast.error(data.message);
      }
      fetchCarriers();
    } catch (err) {
      toast.error(extractApiError(err, 'Erro no teste de conexão'));
    } finally {
      setTesting(false);
    }
  }

  async function handleRemoveCredentials() {
    try {
      const { data } = await api.delete(`/freight/carriers/${selected.id}/credentials`);
      // envelope: { success, data: null, message }
      toast.success(data.message);
      setConfirmRemove(false);
      fetchCarriers();
    } catch (err) {
      toast.error(extractApiError(err, 'Erro ao remover credenciais'));
    }
  }

  const col = selected ? (CARRIER_COLORS[selected.slug] || defaultColors()) : defaultColors();

  const enabledCount = carriers.filter((c) => c.isEnabled).length;
  const withOwnCreds = carriers.filter((c) => c.useOwnCredentials && c.hasCredentials).length;

  return (
    <div>
      {/* Header */}
      <div style={S.pageHeader}>
        <div>
          <h1 style={S.pageTitle}>Módulo de Fretes</h1>
          <p style={S.pageDesc}>Gerencie as transportadoras e configure suas credenciais de acesso</p>
        </div>
        <div style={S.headerStats}>
          <div style={S.headerStat}>
            <span style={S.headerStatValue}>{enabledCount}</span>
            <span style={S.headerStatLabel}>Ativas</span>
          </div>
          <div style={S.headerStatDivider} />
          <div style={S.headerStat}>
            <span style={S.headerStatValue}>{withOwnCreds}</span>
            <span style={S.headerStatLabel}>Credenciais próprias</span>
          </div>
          <div style={S.headerStatDivider} />
          <div style={S.headerStat}>
            <span style={S.headerStatValue}>{carriers.length}</span>
            <span style={S.headerStatLabel}>Disponíveis</span>
          </div>
        </div>
      </div>

      <div style={S.layout}>
        {/* Lista de transportadoras */}
        <div style={S.sidebar}>
          <div style={S.sidebarHeader}>Transportadoras</div>

          {loading ? (
            <div style={S.loadingList}>
              {[1, 2, 3, 4].map((i) => <div key={i} style={S.skeleton} />)}
            </div>
          ) : (
            <div style={S.carrierList}>
              {carriers.map((carrier) => {
                const c = CARRIER_COLORS[carrier.slug] || defaultColors();
                const isSelected = selected?.id === carrier.id;
                return (
                  <div
                    key={carrier.id}
                    style={{
                      ...S.carrierCard,
                      ...(isSelected ? { ...S.carrierCardActive, borderColor: c.accent } : {}),
                    }}
                    onClick={() => { setSelected(carrier); setPanel('config'); }}
                  >
                    <div style={S.carrierCardLeft}>
                      <div style={{ ...S.carrierIcon, background: c.bg, color: c.accent }}>
                        {CARRIER_ICONS[carrier.slug] || '🚛'}
                      </div>
                      <div>
                        <div style={S.carrierName}>{carrier.name}</div>
                        <div style={S.carrierMeta}>
                          {carrier.useOwnCredentials && carrier.hasCredentials
                            ? <span style={{ ...S.credBadge, color: '#7c3aed', background: '#f5f3ff' }}>Chaves próprias</span>
                            : <span style={{ ...S.credBadge, color: '#6b7280', background: '#f3f4f6' }}>Chaves do sistema</span>
                          }
                          {carrier.lastTestOk !== null && (
                            <span style={{ ...S.credBadge, ...(carrier.lastTestOk ? S.testOk : S.testFail) }}>
                              {carrier.lastTestOk ? '✓ Testada' : '✗ Falhou'}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    <button
                      style={{ ...S.toggleBtn, ...(carrier.isEnabled ? S.toggleOn : S.toggleOff) }}
                      onClick={(e) => handleToggle(carrier, e)}
                      title={carrier.isEnabled ? 'Clique para desativar' : 'Clique para ativar'}
                    >
                      <span style={{ ...S.toggleThumb, ...(carrier.isEnabled ? S.toggleThumbOn : {}) }} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Painel de configuração */}
        <div style={S.panel}>
          {!selected ? (
            <div style={S.emptyPanel}>
              <span style={S.emptyIcon}>🚛</span>
              <h3 style={S.emptyTitle}>Selecione uma transportadora</h3>
              <p style={S.emptyDesc}>
                Escolha uma transportadora na lista para configurar sua integração e credenciais de acesso.
              </p>
            </div>
          ) : (
            <>
              {/* Header do painel */}
              <div style={{ ...S.panelHeader, background: col.bg, borderColor: col.border }}>
                <div style={S.panelHeaderLeft}>
                  <div style={{ ...S.panelIcon, color: col.accent }}>
                    {CARRIER_ICONS[selected.slug] || '🚛'}
                  </div>
                  <div>
                    <h2 style={S.panelTitle}>{selected.name}</h2>
                    <p style={S.panelSubtitle}>
                      Tipo de integração: <strong>{selected.apiType?.toUpperCase()}</strong>
                    </p>
                  </div>
                </div>

                <div style={S.panelHeaderRight}>
                  {selected.lastTestedAt && (
                    <div style={S.lastTest}>
                      <span style={selected.lastTestOk ? S.testDotOk : S.testDotFail} />
                      <span style={S.lastTestText}>
                        Último teste: {new Date(selected.lastTestedAt).toLocaleDateString('pt-BR')}
                      </span>
                    </div>
                  )}
                  <button
                    style={{ ...S.testBtn, borderColor: col.accent, color: col.accent }}
                    onClick={handleTest}
                    disabled={testing}
                  >
                    {testing ? '⏳ Testando...' : '🔌 Testar conexão'}
                  </button>
                </div>
              </div>

              {/* Tabs */}
              <div style={S.tabs}>
                <button
                  style={{ ...S.tab, ...(panel === 'config' ? { ...S.tabActive, borderBottomColor: col.accent, color: col.accent } : {}) }}
                  onClick={() => setPanel('config')}
                >
                  ⚙️ Configuração geral
                </button>
                <button
                  style={{ ...S.tab, ...(panel === 'credentials' ? { ...S.tabActive, borderBottomColor: col.accent, color: col.accent } : {}) }}
                  onClick={() => setPanel('credentials')}
                >
                  🔑 Credenciais de acesso
                  {selected.hasCredentials && (
                    <span style={{ ...S.credDot, background: col.accent }} />
                  )}
                </button>
                {selected.slug === 'distancia-mapbox' && (
                  <button
                    style={{ ...S.tab, ...(panel === 'brackets' ? { ...S.tabActive, borderBottomColor: col.accent, color: col.accent } : {}) }}
                    onClick={() => setPanel('brackets')}
                  >
                    📏 Faixas de KM
                  </button>
                )}
              </div>

              {/* Tab: Configuração Geral */}
              {panel === 'config' && (
                <ConfigPanel
                  carrier={selected}
                  onSave={handleSaveConfig}
                  saving={saving}
                  accentColor={col.accent}
                />
              )}

              {/* Tab: Credenciais */}
              {panel === 'credentials' && (
                <div style={S.tabContent}>
                  <CredentialModeSelector
                    carrier={selected}
                    accentColor={col.accent}
                    onSwitchToSystem={() => setConfirmRemove(true)}
                  />

                  {selected.useOwnCredentials || !selected.hasCredentials ? (
                    selected.credentialFields.length > 0 ? (
                      selected.slug === 'distancia-mapbox' ? (
                        <DistanciaCredentialForm
                          carrierId={selected.id}
                          fields={selected.credentialFields}
                          onSave={handleSaveCredentials}
                          saving={saving}
                          accentColor={col.accent}
                        />
                      ) : (
                        <CredentialForm
                          fields={selected.credentialFields}
                          onSave={handleSaveCredentials}
                          saving={saving}
                          accentColor={col.accent}
                        />
                      )
                    ) : (
                      <div style={S.noFields}>
                        ⚠️ Esta transportadora ainda não possui campos de credencial cadastrados.
                      </div>
                    )
                  ) : (
                    <div style={S.usingSystem}>
                      <span style={S.usingSystemIcon}>🏢</span>
                      <p style={S.usingSystemText}>
                        Esta transportadora está usando as <strong>chaves padrão do sistema</strong>.
                        Suas cotações serão feitas com as credenciais globais da plataforma BringHub Frete.
                      </p>
                      <button
                        style={{ ...S.btnOutline, borderColor: col.accent, color: col.accent }}
                        onClick={() => setPanel('credentials') || setSelected({ ...selected, useOwnCredentials: true })}
                      >
                        Usar minhas próprias chaves
                      </button>
                    </div>
                  )}
                </div>
              )}

              {/* Tab: Faixas de KM (apenas Frete por Distância) */}
              {panel === 'brackets' && selected.slug === 'distancia-mapbox' && (
                <DistanceBracketsPanel accentColor={col.accent} />
              )}
            </>
          )}
        </div>
      </div>

      {confirmRemove && (
        <ConfirmModal
          title="Remover credenciais próprias"
          message={`Suas credenciais da ${selected?.name} serão removidas e o sistema voltará a usar as chaves padrão da plataforma.`}
          onConfirm={handleRemoveCredentials}
          onCancel={() => setConfirmRemove(false)}
          confirmLabel="Remover e usar sistema"
          danger
        />
      )}
    </div>
  );
}

// ── Subcomponente: Configuração geral ─────────────────────────

function ConfigPanel({ carrier, onSave, saving, accentColor }) {
  const [isEnabled, setIsEnabled] = useState(carrier.isEnabled);
  const [notes, setNotes] = useState(carrier.notes || '');

  useEffect(() => {
    setIsEnabled(carrier.isEnabled);
    setNotes(carrier.notes || '');
  }, [carrier.id]); // eslint-disable-line

  return (
    <div style={S.tabContent}>
      <div style={S.configSection}>
        <h3 style={S.configSectionTitle}>Status da transportadora</h3>
        <p style={S.configSectionDesc}>
          Quando ativa, esta transportadora será incluída nas cotações de frete do seu painel e API.
        </p>
        <label style={S.switchRow}>
          <div style={S.switchInfo}>
            <span style={S.switchLabel}>
              {isEnabled ? '✅ Transportadora ativa' : '⛔ Transportadora desativada'}
            </span>
            <span style={S.switchDesc}>
              {isEnabled
                ? 'Incluída nas cotações automáticas'
                : 'Oculta nas cotações automáticas'}
            </span>
          </div>
          <div
            style={{ ...S.toggleBtn, ...(isEnabled ? { ...S.toggleOn, background: accentColor } : S.toggleOff) }}
            onClick={() => setIsEnabled(!isEnabled)}
          >
            <span style={{ ...S.toggleThumb, ...(isEnabled ? S.toggleThumbOn : {}) }} />
          </div>
        </label>
      </div>

      <div style={S.configSection}>
        <h3 style={S.configSectionTitle}>Observações</h3>
        <p style={S.configSectionDesc}>Anotações internas sobre esta transportadora (não visível ao cliente final).</p>
        <textarea
          style={S.textarea}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Ex: Usar apenas para envios acima de 5kg no Sudeste..."
          rows={3}
        />
      </div>

      <div style={S.configFooter}>
        <button
          style={{ ...S.btnPrimary, background: accentColor }}
          onClick={() => onSave({ isEnabled, notes })}
          disabled={saving}
        >
          {saving ? 'Salvando...' : 'Salvar configurações'}
        </button>
      </div>
    </div>
  );
}

// ── Subcomponente: Seletor de modo de credencial ───────────────

function CredentialModeSelector({ carrier, accentColor, onSwitchToSystem }) {
  const usingOwn = carrier.useOwnCredentials && carrier.hasCredentials;

  return (
    <div style={S.modeSelector}>
      <div style={S.modeSelectorTitle}>Origem das credenciais</div>
      <div style={S.modeCards}>
        <div style={{ ...S.modeCard, ...(usingOwn ? {} : S.modeCardActive) }}>
          <span style={S.modeCardIcon}>🏢</span>
          <div>
            <div style={S.modeCardLabel}>Chaves do sistema</div>
            <div style={S.modeCardDesc}>Usa as credenciais padrão da plataforma BringHub Frete</div>
          </div>
          {!usingOwn && <span style={{ ...S.modeActiveDot, background: accentColor }} />}
        </div>
        <div style={{ ...S.modeCard, ...(usingOwn ? S.modeCardActive : {}) }}>
          <span style={S.modeCardIcon}>🔑</span>
          <div>
            <div style={S.modeCardLabel}>Minhas chaves</div>
            <div style={S.modeCardDesc}>Usa as credenciais do seu contrato próprio com a transportadora</div>
          </div>
          {usingOwn && <span style={{ ...S.modeActiveDot, background: accentColor }} />}
        </div>
      </div>
      {usingOwn && (
        <button style={S.switchToSystemBtn} onClick={onSwitchToSystem}>
          ← Voltar a usar chaves do sistema
        </button>
      )}
    </div>
  );
}

// ── Subcomponente: Faixas de KM (Frete por Distância) ──────────

const TIPO_LABELS = { por_km: 'R$ por KM (× distância total)', fixo: 'Valor fixo' };

function DistanceBracketsPanel({ accentColor }) {
  const [brackets, setBrackets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/freight/distance-brackets');
      const list = data.data?.brackets || [];
      setBrackets(list.map((b) => ({
        kmDe: String(b.km_de), kmAte: String(b.km_ate), tipo: b.tipo, valor: String(b.valor),
        prazo: b.prazo || '',
      })));
    } catch (err) {
      toast.error(extractApiError(err, 'Erro ao carregar faixas de KM'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  function addRow() {
    setBrackets((rows) => [...rows, { kmDe: '', kmAte: '', tipo: 'por_km', valor: '', prazo: '' }]);
  }
  function removeRow(idx) {
    setBrackets((rows) => rows.filter((_, i) => i !== idx));
  }
  function updateRow(idx, field, value) {
    setBrackets((rows) => rows.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
  }

  async function handleSave() {
    const parsed = brackets.map((b) => ({
      kmDe: Number(b.kmDe), kmAte: Number(b.kmAte), tipo: b.tipo, valor: Number(b.valor),
      prazo: b.prazo?.trim() || null,
    }));
    if (parsed.some((b) => Number.isNaN(b.kmDe) || Number.isNaN(b.kmAte) || Number.isNaN(b.valor) || b.kmAte < b.kmDe)) {
      toast.error('Preencha as faixas corretamente (km de fim deve ser maior ou igual ao km de início)');
      return;
    }
    setSaving(true);
    try {
      await api.put('/freight/distance-brackets', { brackets: parsed });
      toast.success('Faixas de KM salvas com sucesso!');
      load();
    } catch (err) {
      toast.error(extractApiError(err, 'Erro ao salvar faixas de KM'));
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={S.tabContent}>
      <div style={S.configSection}>
        <h3 style={S.configSectionTitle}>Faixas de distância e valor</h3>
        <p style={S.configSectionDesc}>
          Defina as faixas de quilometragem total (origem → destino) e o valor cobrado.
          Ex: de 0 a 10 km, R$ 5,00 por km rodado (total 25km na faixa 11-50km × R$15 = R$375,00).
        </p>

        {loading ? (
          <p style={{ color: '#9ca3af', fontSize: '13px' }}>Carregando...</p>
        ) : (
          <>
            {brackets.map((b, idx) => (
              <div key={idx} style={S.bracketRow}>
                <div style={{ ...S.bracketField, flex: '1 1 90px' }}>
                  <span style={S.bracketLabel}>KM de</span>
                  <input style={S.bracketInput} type="number" min="0" step="0.1" value={b.kmDe} onChange={(e) => updateRow(idx, 'kmDe', e.target.value)} placeholder="0" />
                </div>
                <div style={{ ...S.bracketField, flex: '1 1 90px' }}>
                  <span style={S.bracketLabel}>KM até</span>
                  <input style={S.bracketInput} type="number" min="0" step="0.1" value={b.kmAte} onChange={(e) => updateRow(idx, 'kmAte', e.target.value)} placeholder="10" />
                </div>
                <div style={{ ...S.bracketField, flex: '1 1 160px' }}>
                  <span style={S.bracketLabel}>Tipo</span>
                  <select style={S.bracketInput} value={b.tipo} onChange={(e) => updateRow(idx, 'tipo', e.target.value)}>
                    {Object.entries(TIPO_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
                <div style={{ ...S.bracketField, flex: '1 1 100px' }}>
                  <span style={S.bracketLabel}>Valor (R$)</span>
                  <input style={S.bracketInput} type="number" min="0" step="0.01" value={b.valor} onChange={(e) => updateRow(idx, 'valor', e.target.value)} placeholder="5.00" />
                </div>
                <div style={{ ...S.bracketField, flex: '2 1 150px' }}>
                  <span style={S.bracketLabel}>Prazo de entrega</span>
                  <input style={S.bracketInput} type="text" value={b.prazo} onChange={(e) => updateRow(idx, 'prazo', e.target.value)} placeholder="Ex: 2 a 5 dias" />
                </div>
                <button style={S.bracketRemoveBtn} onClick={() => removeRow(idx)} title="Remover faixa">✕</button>
              </div>
            ))}

            {brackets.length === 0 && (
              <p style={{ color: '#9ca3af', fontSize: '13px', margin: '12px 0' }}>Nenhuma faixa configurada ainda.</p>
            )}

            <button style={{ ...S.btnOutline, borderColor: accentColor, color: accentColor, marginTop: '12px' }} onClick={addRow}>
              + Adicionar faixa
            </button>
          </>
        )}
      </div>

      <div style={S.configFooter}>
        <button style={{ ...S.btnPrimary, background: accentColor }} onClick={handleSave} disabled={saving || loading}>
          {saving ? 'Salvando...' : 'Salvar faixas de KM'}
        </button>
      </div>
    </div>
  );
}

// ── Estilos ───────────────────────────────────────────────────

const S = {
  pageHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    marginBottom: '24px', flexWrap: 'wrap', gap: '16px',
  },
  pageTitle: { fontSize: '24px', fontWeight: '700', color: '#111827' },
  pageDesc: { fontSize: '14px', color: '#6b7280', marginTop: '4px' },
  headerStats: {
    display: 'flex', alignItems: 'center', gap: '20px',
    background: '#fff', border: '1px solid #e5e7eb',
    borderRadius: '10px', padding: '12px 20px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  headerStat: { textAlign: 'center' },
  headerStatValue: { display: 'block', fontSize: '20px', fontWeight: '700', color: '#111827' },
  headerStatLabel: { fontSize: '11px', color: '#9ca3af', fontWeight: '500' },
  headerStatDivider: { width: '1px', height: '32px', background: '#e5e7eb' },

  layout: {
    display: 'grid',
    gridTemplateColumns: '300px 1fr',
    gap: '20px',
    alignItems: 'start',
  },

  // Sidebar lista
  sidebar: {
    background: '#fff',
    borderRadius: '12px',
    border: '1px solid #e5e7eb',
    overflow: 'hidden',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  sidebarHeader: {
    padding: '14px 16px',
    fontSize: '12px', fontWeight: '700', color: '#6b7280',
    textTransform: 'uppercase', letterSpacing: '0.8px',
    borderBottom: '1px solid #f3f4f6',
    background: '#f9fafb',
  },
  loadingList: { padding: '12px', display: 'flex', flexDirection: 'column', gap: '10px' },
  skeleton: { height: '64px', background: '#f3f4f6', borderRadius: '8px', animation: 'pulse 1.5s infinite' },
  carrierList: { display: 'flex', flexDirection: 'column' },
  carrierCard: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '14px 16px', cursor: 'pointer',
    borderLeft: '3px solid transparent',
    borderBottom: '1px solid #f9fafb',
    transition: 'background 0.12s, border-color 0.12s',
  },
  carrierCardActive: { background: '#f8faff', borderLeft: '3px solid' },
  carrierCardLeft: { display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 },
  carrierIcon: {
    width: '38px', height: '38px', borderRadius: '10px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '18px', flexShrink: 0,
  },
  carrierName: { fontSize: '14px', fontWeight: '600', color: '#111827' },
  carrierMeta: { display: 'flex', gap: '6px', marginTop: '3px', flexWrap: 'wrap' },
  credBadge: {
    fontSize: '10px', fontWeight: '600', padding: '1px 7px',
    borderRadius: '10px', whiteSpace: 'nowrap',
  },
  testOk: { color: '#16a34a', background: '#f0fdf4' },
  testFail: { color: '#dc2626', background: '#fef2f2' },

  // Toggle switch
  toggleBtn: {
    width: '36px', height: '20px', borderRadius: '10px',
    padding: '2px', cursor: 'pointer',
    border: 'none', transition: 'background 0.2s',
    flexShrink: 0, position: 'relative',
  },
  toggleOn: { background: '#16a34a' },
  toggleOff: { background: '#d1d5db' },
  toggleThumb: {
    display: 'block', width: '16px', height: '16px',
    borderRadius: '50%', background: '#fff',
    transition: 'transform 0.2s',
    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
  },
  toggleThumbOn: { transform: 'translateX(16px)' },

  // Painel direito
  panel: {
    background: '#fff', borderRadius: '12px',
    border: '1px solid #e5e7eb',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    overflow: 'hidden',
  },
  emptyPanel: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    padding: '80px 40px', textAlign: 'center',
  },
  emptyIcon: { fontSize: '48px', marginBottom: '16px' },
  emptyTitle: { fontSize: '18px', fontWeight: '700', color: '#111827', marginBottom: '8px' },
  emptyDesc: { fontSize: '14px', color: '#6b7280', lineHeight: 1.6, maxWidth: '320px' },

  panelHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '20px 24px', border: '0 0 1px 0', borderStyle: 'solid',
    flexWrap: 'wrap', gap: '12px',
  },
  panelHeaderLeft: { display: 'flex', alignItems: 'center', gap: '14px' },
  panelIcon: { fontSize: '32px' },
  panelTitle: { fontSize: '20px', fontWeight: '700', color: '#111827' },
  panelSubtitle: { fontSize: '13px', color: '#6b7280', marginTop: '2px' },
  panelHeaderRight: { display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' },

  lastTest: { display: 'flex', alignItems: 'center', gap: '6px' },
  testDotOk: { width: '8px', height: '8px', borderRadius: '50%', background: '#16a34a', flexShrink: 0 },
  testDotFail: { width: '8px', height: '8px', borderRadius: '50%', background: '#dc2626', flexShrink: 0 },
  lastTestText: { fontSize: '12px', color: '#6b7280' },
  testBtn: {
    padding: '8px 16px', background: '#fff', border: '1.5px solid',
    borderRadius: '7px', fontSize: '13px', fontWeight: '600',
    cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap',
  },

  // Tabs
  tabs: {
    display: 'flex', borderBottom: '1px solid #f3f4f6',
    padding: '0 24px',
  },
  tab: {
    padding: '14px 4px', marginRight: '24px',
    background: 'none', border: 'none', borderBottom: '2px solid transparent',
    fontSize: '14px', fontWeight: '500', color: '#6b7280',
    cursor: 'pointer', fontFamily: 'inherit',
    display: 'flex', alignItems: 'center', gap: '6px',
    transition: 'color 0.15s',
  },
  tabActive: { fontWeight: '600' },
  credDot: {
    width: '7px', height: '7px', borderRadius: '50%',
    display: 'inline-block', marginLeft: '2px',
  },
  tabContent: { padding: '24px' },

  // Config panel
  configSection: {
    marginBottom: '28px',
    paddingBottom: '28px',
    borderBottom: '1px solid #f3f4f6',
  },
  configSectionTitle: { fontSize: '15px', fontWeight: '600', color: '#111827', marginBottom: '4px' },
  configSectionDesc: { fontSize: '13px', color: '#6b7280', marginBottom: '16px' },
  switchRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    background: '#f9fafb', borderRadius: '10px', padding: '14px 16px',
    cursor: 'pointer', gap: '12px',
  },
  switchInfo: { display: 'flex', flexDirection: 'column', gap: '3px' },
  switchLabel: { fontSize: '14px', fontWeight: '600', color: '#111827' },
  switchDesc: { fontSize: '12px', color: '#6b7280' },
  textarea: {
    width: '100%', padding: '10px 14px',
    border: '1.5px solid #e5e7eb', borderRadius: '8px',
    fontSize: '14px', fontFamily: 'inherit', color: '#111827',
    background: '#f9fafb', resize: 'vertical', outline: 'none',
    boxSizing: 'border-box',
  },
  configFooter: { display: 'flex', justifyContent: 'flex-end', marginTop: '4px' },
  btnPrimary: {
    padding: '10px 24px', color: '#fff', border: 'none',
    borderRadius: '8px', fontSize: '14px', fontWeight: '600',
    cursor: 'pointer', fontFamily: 'inherit',
  },

  // Credential mode selector
  modeSelector: { marginBottom: '24px' },
  modeSelectorTitle: { fontSize: '13px', fontWeight: '600', color: '#6b7280', marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.5px' },
  modeCards: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '12px' },
  modeCard: {
    display: 'flex', alignItems: 'flex-start', gap: '10px',
    padding: '14px', border: '1.5px solid #e5e7eb',
    borderRadius: '10px', position: 'relative',
    background: '#f9fafb',
  },
  modeCardActive: { border: '1.5px solid', background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.08)' },
  modeCardIcon: { fontSize: '20px', flexShrink: 0 },
  modeCardLabel: { fontSize: '13px', fontWeight: '600', color: '#111827' },
  modeCardDesc: { fontSize: '12px', color: '#6b7280', marginTop: '2px', lineHeight: 1.4 },
  modeActiveDot: {
    position: 'absolute', top: '10px', right: '10px',
    width: '8px', height: '8px', borderRadius: '50%',
  },
  switchToSystemBtn: {
    background: 'none', border: 'none',
    fontSize: '13px', color: '#6b7280', cursor: 'pointer',
    fontFamily: 'inherit', textDecoration: 'underline', padding: 0,
  },

  // Estado "usando sistema"
  usingSystem: {
    textAlign: 'center', padding: '32px 24px',
    background: '#f9fafb', borderRadius: '10px',
    border: '1.5px dashed #e5e7eb',
  },
  usingSystemIcon: { fontSize: '36px', display: 'block', marginBottom: '12px' },
  usingSystemText: { fontSize: '14px', color: '#6b7280', lineHeight: 1.6, maxWidth: '360px', margin: '0 auto 20px' },
  btnOutline: {
    padding: '9px 20px', background: '#fff', border: '1.5px solid',
    borderRadius: '8px', fontSize: '14px', fontWeight: '600',
    cursor: 'pointer', fontFamily: 'inherit',
  },

  noFields: {
    background: '#fffbeb', border: '1px solid #fde68a',
    borderRadius: '8px', padding: '14px 16px',
    fontSize: '14px', color: '#92400e',
  },

  bracketRow: {
    display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '10px', alignItems: 'center',
    padding: '10px', border: '1px solid #f3f4f6', borderRadius: '8px', background: '#fafafa',
  },
  bracketField: {
    flex: '1 1 130px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: '3px',
  },
  bracketLabel: {
    fontSize: '10px', fontWeight: '700', color: '#9ca3af',
    textTransform: 'uppercase', letterSpacing: '0.5px',
  },
  bracketInput: {
    width: '100%', boxSizing: 'border-box', padding: '8px 10px', border: '1.5px solid #e5e7eb', borderRadius: '7px',
    fontSize: '13px', fontFamily: 'inherit', color: '#111827', background: '#fff', outline: 'none',
  },
  bracketRemoveBtn: {
    width: '32px', height: '32px', flexShrink: 0, alignSelf: 'flex-end',
    background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca',
    borderRadius: '7px', cursor: 'pointer', fontSize: '13px', fontWeight: '700',
  },
};
